import { useEffect, useState } from "react";
import { Loader2, Check, X, MinusCircle, Pill } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const STATUS_STYLE = {
  administered: { c: "#12B76A", label: "Administered" },
  missed: { c: "#F04438", label: "Missed" },
  refused: { c: "#F79009", label: "Refused" },
  due: { c: "#155EEF", label: "Due" },
};

export default function Medications() {
  const [mar, setMar] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/mar").then((r) => setMar(r.data)).catch(() => setMar([]));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/mar/${id}/administer`, { action });
      toast.success(`Marked ${action}`);
      load();
    } catch (e) { toast.error("Action failed"); }
  };

  if (!mar) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const counts = mar.reduce((a, m) => { a[m.status] = (a[m.status] || 0) + 1; return a; }, {});
  const filtered = filter === "all" ? mar : mar.filter((m) => m.status === filter);

  return (
    <div>
      <PageHeader overline="Clinical · eMAR" title="Medication Administration"
        description="Electronic MAR with administration verification and safety intelligence. AI never changes medication orders." />

      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={Check} label="Administered" value={counts.administered || 0} accent="#12B76A" index={0} onClick={() => setFilter("administered")} />
        <StatCard icon={X} label="Missed" value={counts.missed || 0} accent="#F04438" index={1} onClick={() => setFilter("missed")} />
        <StatCard icon={MinusCircle} label="Refused" value={counts.refused || 0} accent="#F79009" index={2} onClick={() => setFilter("refused")} />
        <StatCard icon={Pill} label="Due Now" value={counts.due || 0} accent="#155EEF" index={3} onClick={() => setFilter("due")} />
      </div>

      <div className="mb-3 flex gap-2">
        {["all", "due", "administered", "missed", "refused"].map((f) => (
          <button key={f} data-testid={`mar-filter-${f}`} onClick={() => setFilter(f)}
            className={cn("rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors",
              filter === f ? "border-[#155EEF] bg-[#155EEF] text-white" : "bg-card hover:border-[#155EEF]")}>
            {f}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="careiq-scroll overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Resident</th><th className="px-4 py-3">Medication</th>
                <th className="px-4 py-3">Time</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((m) => {
                const s = STATUS_STYLE[m.status] || STATUS_STYLE.due;
                return (
                  <tr key={m.id} data-testid={`mar-row-${m.id}`} className="transition-colors hover:bg-muted/40">
                    <td className="px-4 py-3 font-medium">{m.resident_name}</td>
                    <td className="px-4 py-3">{m.med_name} <span className="text-muted-foreground">{m.dose}</span></td>
                    <td className="px-4 py-3 text-muted-foreground">{m.scheduled_time}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                        style={{ color: s.c, backgroundColor: `${s.c}1a` }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.c }} />{s.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button data-testid={`administer-${m.id}`} onClick={() => act(m.id, "administered")} title="Administer"
                          className="rounded-md border px-2 py-1 text-xs font-medium text-[#12B76A] transition-colors hover:bg-[#12B76A] hover:text-white">
                          <Check size={14} />
                        </button>
                        <button onClick={() => act(m.id, "refused")} title="Refused"
                          className="rounded-md border px-2 py-1 text-xs font-medium text-[#F79009] transition-colors hover:bg-[#F79009] hover:text-white">
                          <MinusCircle size={14} />
                        </button>
                        <button onClick={() => act(m.id, "missed")} title="Missed"
                          className="rounded-md border px-2 py-1 text-xs font-medium text-[#F04438] transition-colors hover:bg-[#F04438] hover:text-white">
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
